import { AfterViewInit, Component, computed, effect, ElementRef, EventEmitter, inject, input, OnDestroy, Output, signal, ViewChild } from '@angular/core';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SafeResourceUrl } from '@angular/platform-browser';
import { base64ToBlob, blobToBuffer, generateRandomWaveform, generateWaveformFromBuffer, WaveformBar } from '../../helpers/audio.helper';
import { CustomContextMenuItem } from '../ui/custom-context-menu/custom-context-menu.component';
import { ContextMenuSelection, CustomContextMenuService } from '../ui/custom-context-menu/custom-context-menu.service';

@Component({
  selector: 'app-audio-player',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, MatMenuModule],
  templateUrl: './audio-player.component.html',
  styleUrl: './audio-player.component.scss',
  host: {
    '[class.contrast]': 'contrast()',
    '[class.compact]': 'compact()',
    '[class.full-width]': 'fullWidth()',
    '[style.--bar-width.px]': 'barWidth()',
    '[style.--bar-gap.px]': 'barGap()',
  },
})
export class AudioPlayerComponent implements AfterViewInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private contextMenuService = inject(CustomContextMenuService);
  private audioBuffer: AudioBuffer | null = null;
  private currentUrl: string | null = null;

  audioSource = input.required<string | SafeResourceUrl>();
  audioSourceUpdating = input(false);
  additionalActions = input<CustomContextMenuItem[]>([]);
  overrideDefaultActions = input<boolean>(false);
  barsCount = input<number>(40);
  barWidth = input<number>(4);
  barGap = input<number>(2);
  contrast = input<boolean>(false);
  fullWidth = input<boolean>(false);
  compact = input<boolean>(false);

  @Output() ended = new EventEmitter<void>();
  @Output() played = new EventEmitter<void>();
  @Output() paused = new EventEmitter<void>();

  // --- View Children ---
  @ViewChild('audioElement') private audioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('optionsButton') public optionsButtonRef!: ElementRef<MatIconButton>;
  @ViewChild('waveformContainer') private waveformContainerRef!: ElementRef<HTMLDivElement>;

  // --- Component State Signals ---
  isPlaying = signal(false);

  duration = signal(0);
  currentTime = signal(0);
  isLoading = signal(true);
  volume = signal(1);
  isMuted = signal(false);

  private rawWaveformBars = signal<WaveformBar[]>([]);
  private effectiveBarsCount = signal(40);
  private resizeObserver?: ResizeObserver;

  durationDisplay = computed(() => this.formatTime(this.duration()));
  timeDisplay = computed(() => this.formatTime(this.currentTime()));

  waveformBars = computed(() => {
    const bars = this.rawWaveformBars();
    const total = this.duration();
    const current = this.currentTime();

    if (total === 0) {
      return bars.map((bar) => ({ ...bar, played: false }));
    }

    const progressPercentage = (current / total) * 100;
    return bars.map((bar, index) => {
      const barPercentage = ((index + 1) / this.effectiveBarsCount()) * 100;
      return {
        ...bar,
        played: barPercentage <= progressPercentage,
      };
    });
  });

  menuItems = computed(() => {
    const additional = this.additionalActions();
    const shouldOverride = this.overrideDefaultActions();
    const defaultActions: CustomContextMenuItem[] = [{ id: 'download', text: 'AudioPlayer.Action.Download', icon: 'download', func: () => this.downloadAudio() }];
    return shouldOverride ? additional : [...defaultActions, ...additional];
  });

  private get audio(): HTMLAudioElement {
    return this.audioRef.nativeElement;
  }

  constructor() {
    effect(() => {
      const source = this.audioSource();
      if (source && this.audioRef) {
        // Unwrap SafeResourceUrl if necessary
        const sourceUrl = typeof source === 'string' ? source : (source as any).changingThisBreaksApplicationSecurity;
        this.loadAudioFromUrl(sourceUrl);
      }
    });

    effect(() => {
      if (!this.fullWidth()) {
        this.effectiveBarsCount.set(this.barsCount());
      }
    });

    effect(() => {
      const count = this.effectiveBarsCount();
      if (this.audioBuffer) {
        this.rawWaveformBars.set(generateWaveformFromBuffer(this.audioBuffer, count));
      } else {
        this.rawWaveformBars.set(generateRandomWaveform(count));
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.audioRef) {
      this.audio.volume = this.volume();
      this.audio.addEventListener('timeupdate', this.onTimeUpdate);
      this.audio.addEventListener('ended', this.onEnded);
      this.audio.addEventListener('durationchange', this.onDurationChange);
      // Trigger initial load if source is already set
      const source = this.audioSource();
      if (source) {
        const sourceUrl = typeof source === 'string' ? source : (source as any).changingThisBreaksApplicationSecurity;
        this.loadAudioFromUrl(sourceUrl);
      }
    } else {
      console.error('Audio element not found!');
    }

    if (this.fullWidth()) {
      this.setupFullWidthBars();
    }
  }

  private setupFullWidthBars(): void {
    const container = this.waveformContainerRef.nativeElement;

    const calculateAndSetBars = () => {
      const containerWidth = container.clientWidth;
      if (containerWidth === 0) return; // Hidden or not layout yet

      // Read values from CSS (controlled by container queries)
      const styles = getComputedStyle(container);
      const barWidth = parseFloat(styles.getPropertyValue('--bar-width').trim()) || 4;
      const barGap = parseFloat(styles.getPropertyValue('--bar-gap').trim()) || 2;

      const effectiveWidth = barWidth + barGap;
      const count = Math.floor(containerWidth / effectiveWidth);

      // Update signal only if changed to avoid unnecessary cycles (though signal handles equality check)
      this.effectiveBarsCount.set(count > 0 ? count : 1);
    };

    // Initial calcluation
    calculateAndSetBars();

    this.resizeObserver = new ResizeObserver(() => {
      // styles might have changed if container size changed crossing a breakpoint
      calculateAndSetBars();
    });

    this.resizeObserver.observe(container);
    // Also observe the host because that's where the container query is attached
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  /**
   * Main method to load audio from a given URL (can be a web URL or a data URL).
   * @param url The string URL of the audio source.
   */
  async loadAudioFromUrl(url: string): Promise<void> {
    if (this.currentUrl === url) {
      return;
    }
    this.currentUrl = url;

    this.resetPlayerState();
    if (!url) {
      this.isLoading.set(false);
      return;
    }

    this.audio.src = url;

    try {
      let audioBlob: Blob;
      // Check if it's a base64 data URL
      if (url.startsWith('data:')) {
        const mimeType = url.substring(url.indexOf(':') + 1, url.indexOf(';'));
        audioBlob = base64ToBlob(url, mimeType);
      } else {
        // Otherwise, treat it as a web URL and fetch it
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch audio: ${response.statusText}`);
        audioBlob = await response.blob();
      }

      // Once we have the blob, process it
      const audioBuffer = await blobToBuffer(audioBlob);
      if (audioBuffer) {
        this.audioBuffer = audioBuffer;
        this.duration.set(audioBuffer.duration);
        this.rawWaveformBars.set(generateWaveformFromBuffer(audioBuffer, this.effectiveBarsCount()));
        this.audio.src = URL.createObjectURL(audioBlob);
      }
    } catch (error) {
      console.error('Error loading or processing audio:', error);
      // If we couldn't decode, fallback to random waveform
      this.rawWaveformBars.set(generateRandomWaveform(this.effectiveBarsCount()));
    } finally {
      this.isLoading.set(false);
    }
  }

  private resetPlayerState(): void {
    this.isLoading.set(true);
    this.isPlaying.set(false);
    this.currentTime.set(0);
    this.duration.set(0);
    this.audioBuffer = null;
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
    }
    // Show a placeholder waveform while loading
    this.rawWaveformBars.set(generateRandomWaveform(this.effectiveBarsCount()));
  }

  private formatTime(seconds: number): string {
    if (isNaN(seconds) || seconds < 0) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  play(): void {
    if (this.audio.paused) {
      this.audio.play().catch((e) => console.error('Error playing audio:', e));
      this.isPlaying.set(true);
      this.played.emit();
    }
  }

  pause(): void {
    if (!this.audio.paused) {
      this.audio.pause();
      this.isPlaying.set(false);
      this.paused.emit();
    }
  }

  isPaused(): boolean {
    return !this.isPlaying();
  }

  togglePlayPause(): void {
    if (this.isPaused()) {
      this.play();
    } else {
      this.pause();
    }
  }

  onTimeUpdate = (): void => this.currentTime.set(this.audio.currentTime);

  onEnded = (): void => {
    this.isPlaying.set(false);
    this.currentTime.set(0); // Reset to beginning
    this.ended.emit();
  };

  onDurationChange = (): void => {
    const d = this.audio.duration;
    if (d && !isNaN(d) && d !== Infinity) {
      this.duration.set(d);
    }
  };

  seek(event: MouseEvent): void {
    const totalDuration = this.duration();
    if (totalDuration > 0) {
      const waveformRect = this.waveformContainerRef.nativeElement.getBoundingClientRect();
      const clickX = event.clientX - waveformRect.left;
      const percentage = clickX / waveformRect.width;
      this.audio.currentTime = totalDuration * percentage;
    }
  }

  onOptionsClick(event: MouseEvent, triggerElement: MatIconButton): void {
    event.stopPropagation();
    if (triggerElement?._elementRef) {
      this.contextMenuService.open(triggerElement._elementRef, this.menuItems()).subscribe((selection: ContextMenuSelection) => {
        // Handle menu selections if needed
      });
    }
  }

  private downloadAudio(): void {
    const source = this.audioSource();
    if (!source) return;

    const sourceUrl = typeof source === 'string' ? source : (source as any).changingThisBreaksApplicationSecurity;

    const link = document.createElement('a');
    link.href = sourceUrl;
    // Provide a generic filename if the URL doesn't imply one
    link.download = `audio-recording-${new Date().toISOString()}.webm`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  ngOnDestroy(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio.removeEventListener('timeupdate', this.onTimeUpdate);
      this.audio.removeEventListener('ended', this.onEnded);
      this.audio.removeEventListener('durationchange', this.onDurationChange);
    }
    this.currentUrl = null;
    this.resizeObserver?.disconnect();
  }
}
